import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMe, actions } from '../store';
import {
    ApplicableRole,
    applyAccess,
    fetchApplicableRoles,
} from '../api/access';
import RolePickMatrix from './RolePickMatrix';
import { ApiError } from '../api/request';
import { auth } from '../utils/auth';

/**
 * 平台访问闸门 (2026-08-25; 2026-09-12 改为"申请权限").
 * passport 登录 ≠ 可访问: 未准入的用户看到的不再是一句"申请使用", 而是可申请的角色清单 ——
 * 普通用户 (全部业务数据只读, 默认勾且不可取消) + 各资源管理员 (勾选即申请该资源的管理权).
 * 提交后生成审批中心工单, 审批通过时准入 + 勾选的角色一并绑定 (后端 access.MarkDecision).
 * 后端 auth.AccessGuard 是真闸门 (业务码 20103), 这里只是前端不给看.
 */
const AccessGate = ({ children }: { children: React.ReactNode }) => {
    const me = useMe();
    const dispatch = useDispatch();
    const [reason, setReason] = useState('');
    const [roles, setRoles] = useState<ApplicableRole[]>([]);
    const [picked, setPicked] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [justApplied, setJustApplied] = useState(false);

    const blocked =
        me.loaded && !(me.isAdmin || me.accessStatus === 'approved');
    useEffect(() => {
        if (!blocked) return;
        fetchApplicableRoles()
            .then((r) => setRoles(r.list || []))
            .catch(() => setRoles([]));
    }, [blocked]);

    if (!me.loaded) {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
                正在校验权限…
            </div>
        );
    }
    if (!blocked) {
        return <>{children}</>;
    }
    const pending = me.accessStatus === 'pending' || justApplied;
    const rejected = me.accessStatus === 'rejected' && !justApplied;
    const userRole = roles.find((r) => r.name === 'user');

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            await applyAccess(reason.trim(), picked);
            setJustApplied(true);
        } catch (err) {
            setError(
                err instanceof ApiError
                    ? `${err.code}: ${err.message}`
                    : String(err),
            );
        } finally {
            setBusy(false);
        }
    };
    const refresh = () => {
        (dispatch as any)(actions.me.load());
        // 审批通过后权限随之变化, 一并刷新生效规则.
        (dispatch as any)(actions.authz.load());
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
            <div className="bg-white rounded-xl shadow w-[640px] max-w-full p-8 space-y-4">
                <div className="text-center space-y-1">
                    <div className="text-4xl">🔐</div>
                    <div className="text-lg font-semibold text-slate-800">
                        工艺参数平台 · 申请权限
                    </div>
                    <div className="text-sm text-slate-500">
                        当前账号:{me.email || me.username || '—'}
                    </div>
                    {/* 账号登错 / 权限申错时的出口: 退出后回 passport 登录页换号 */}
                    <button
                        type="button"
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => auth.logout()}
                    >
                        不是当前账号?退出登录 / 切换账号
                    </button>
                </div>

                {pending && (
                    <div className="text-center space-y-3">
                        <div className="text-sm text-slate-700">
                            申请已提交,管理员将在审批中心处理。审批通过后刷新即可进入平台。
                        </div>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={refresh}
                        >
                            刷新状态
                        </button>
                    </div>
                )}

                {!pending && (
                    <form onSubmit={submit} className="space-y-4">
                        {rejected && (
                            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">
                                上次申请已被驳回,可调整所需权限与理由后重新提交。
                            </div>
                        )}
                        <div className="text-sm text-slate-600">
                            本平台仅限内部使用。普通用户默认只能看自己的工单和
                            API Token,请按资源勾选需要的只读 /
                            管理权限,管理员审批通过后生效:
                        </div>

                        <RolePickMatrix
                            roles={roles}
                            picked={picked}
                            onChange={setPicked}
                            userRole={userRole || null}
                            loading={roles.length === 0}
                        />

                        <div>
                            <div className="mb-1 text-xs text-slate-500">
                                申请理由(必填,最多 200 字)
                            </div>
                            <textarea
                                className="inp w-full h-20"
                                maxLength={200}
                                placeholder="例如:负责 XX 系列材料的参数录入,需要材料 / 工艺参数管理权限"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                        {error && (
                            <div className="text-sm text-rose-600">{error}</div>
                        )}
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-slate-400">
                                {picked.length > 0
                                    ? `已选 普通用户 + ${picked.length} 项权限${reason.trim() ? '' : ';请填写申请理由'}`
                                    : '请至少勾选一项模块只读或资源管理员权限'}
                            </div>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={
                                    busy ||
                                    picked.length === 0 ||
                                    !reason.trim()
                                }
                                title={
                                    picked.length === 0
                                        ? '先勾选需要的权限'
                                        : undefined
                                }
                            >
                                {busy ? '提交中…' : '提交申请'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AccessGate;
